import ArrowRightAltIcon from "@mui/icons-material/ArrowRightAlt";
import AssignmentIcon from "@mui/icons-material/Assignment";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import GppMaybeIcon from "@mui/icons-material/GppMaybe";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import RemoveRedEyeOutlinedIcon from "@mui/icons-material/RemoveRedEyeOutlined";
import {
  Box,
  Container,
  FormControl,
  Grid,
  Link,
  Menu,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  useTheme,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import ContractInfo from "../../components/Contract/ContractInfo";
import MoreInfo from "../../components/Contract/MoreInfo";
import Title from "../../components/Pages/Title";
import Pagination from "../../components/Pagination";
import { useUpcxChain } from "../../hook/useUpcxChain";
import { CalculatePassTime } from "../../utils";
import DvrIcon from "@mui/icons-material/Dvr";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import FolderTree from "react-folder-tree";
import CodeIcon from "@mui/icons-material/Code";
import ListIcon from "@mui/icons-material/List";
import axios from "axios";
import { useRecoilState } from "recoil";
import { ShowProgress } from "../../state/ShowProgress";
import DescriptionIcon from "@mui/icons-material/Description";
import { ReadContractItem } from "./ReadContractItem";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import { WriteContractItem } from "./WriteContractItem";
import { ExtensionHandler } from "upcx-js";
import { LOCAL_STORAGE_KEY } from "../../common/const";
import { toast } from "react-toastify";
import {
  Contract,
  ContractService,
  Transaction,
  TransactionsService,
} from "../../backend";

const TAB = {
  TRANSACTION: 0,
  CONTRACT: 1,
  EVENTS: 2,
  ANALYTICS: 3,
};

const SUB_TAB = {
  CODE: 1,
  READ_CONTRACT: 2,
  WRITE_CONTRACT: 3,
};

export const ContractDetail = () => {
  const { contractName } = useParams();

  const { quoteUSD, selectedShard } = useUpcxChain();

  const [, setShowProgress] = useRecoilState(ShowProgress);

  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [contract, setContract] = useState<Contract>();

  const [abi, setAbi] = useState<any>();

  const [sourceStructure, setSourceStructure] = useState<any>();
  const [selectedFilePath, setSelectedFilePath] = useState<string>();
  const [selectedFileContent, setSelectedFileContent] = useState<string>("");

  const [limit, setLimit] = useState<number>(25);
  const [total, setTotal] = useState<number>(0);
  const [page, setPage] = useState<number>(0);

  const [selectedTab, setSelectedTab] = useState<number>(TAB.TRANSACTION);
  const [selectedSubTab, setSelectedSubTab] = useState<number>(
    SUB_TAB.READ_CONTRACT
  );

  const [initialized, setInitialized] = useState<boolean>(false);
  const [connectedAddress, setConnectedAddress] = useState<string>();

  useEffect(() => {
    if (!initialized) {
      setInitialized(true);
      return;
    }
    console.log("Initialized");

    const handler = ExtensionHandler.instance();

    const extensionID =
      localStorage.getItem(LOCAL_STORAGE_KEY.EXTENSION_ID) ?? "";

    if (extensionID) {
      handler.setExtensionID(extensionID);
    }

    handler.getCurrentChainID().then((res) => {
      if (!res.result) {
        return;
      }

      if (res.data.chainID !== parseInt(process.env.REACT_APP_CHAIN_ID!)) {
        resetConnection();
      }
    });

    handler
      .getCurrentAccount(parseInt(process.env.REACT_APP_CHAIN_ID!))
      .then(async (res) => {
        if (!res.result) {
          resetConnection();
          return;
        }

        setConnectedAddress(res.data.account);
      })
      .catch((err) => {
        resetConnection();
      });

    handler.setNetworkChangeHandler(handleNetworkChanged);
    handler.setConnectHandler(handleConnected);
    handler.setDisconnetHandler(handleDisconnected);
    handler.setAccountChangeHandler(handleAccountChanged);
    handler.setRejectConnectionHandler(handleRejectConnection);

    handler.startListen();
  }, [initialized, connectedAddress]);

  useEffect(() => {
    if (!selectedShard) {
      return;
    }

    getTransactions();
    getContract();
  }, [contractName, limit, page, selectedShard]);

  useEffect(() => {
    if (contract?.verified) {
      axios
        .get(`${process.env.REACT_APP_PUBLIC_API_URL}/${contract.abiPath}`)
        .then((res) => {
          setAbi(res.data);
        })
        .catch((err) => {
          console.log(err);
        });

      initSourceStructure();
    }
  }, [contract]);

  useEffect(() => {
    if (!selectedFilePath) {
      setSelectedFileContent("");
      return;
    }

    setShowProgress(true);
    axios
      .get(selectedFilePath)
      .then((res) => {
        setShowProgress(false);
        setSelectedFileContent(res.data);
      })
      .catch((err) => {
        setShowProgress(false);
        setSelectedFileContent("");
        console.log(err);
      });
  }, [selectedFilePath]);

  const handleChange = (event: SelectChangeEvent) => {
    setLimit(parseInt(event.target.value));
    setPage(0);
  };

  const getTransactions = async () => {
    if (!contractName) return;
    try {
      const result = await TransactionsService.getTransactionsByAccount(
        contractName,
        0,
        undefined,
        undefined,
        limit,
        page,
        "DESC",
        "blockNum"
      );
      setTransactions(result.txs ?? []);
      setTotal(result.total ?? 0);
    } catch (err) {
      console.log(err);
    }
  };

  const getContract = () => {
    if (!contractName) return;

    ContractService.getContract(contractName)
      .then((res) => {
        if (!res) return;

        setContract(res);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const handleSourceFileClicked = (opts: any) => {
    if (opts.nodeData.type === "directory") {
      return;
    }

    const elements = document.getElementsByClassName("displayName");

    for (let i = 0; i < elements.length; i++) {
      if ((elements[i] as any).innerText === opts.nodeData.name) {
        const treeElements = document.getElementsByClassName("TreeNode");

        for (let j = 0; j < treeElements.length; j++) {
          treeElements[j].classList.remove("folder-element-active");
        }

        elements[i].parentElement?.parentElement?.parentElement?.classList.add(
          "folder-element-active"
        );
        break;
      }
    }

    let filePath =
      process.env.REACT_APP_PUBLIC_API_URL + "/" + contract?.sourcePath + "/";
    let folderNode = sourceStructure;
    for (let i = 0; i < opts.nodeData.path.length; i++) {
      filePath =
        filePath + folderNode.children[opts.nodeData.path[i]].name + "/";

      folderNode = folderNode.children[opts.nodeData.path[i]];
    }

    setSelectedFilePath(filePath.slice(0, filePath.length - 1));
  };

  const composeStructure = (items: any[]): any => {
    return items.map((item: any, index: number) => {
      if (item.type === "directory") {
        return {
          checked: 0,
          children: composeStructure(item.children),
          isOpen: false,
          name: item.name,
          id: 0,
          type: "directory",
        };
      } else {
        return {
          checked: 0,
          children: undefined,
          isOpen: false,
          name: item.name,
          id: 0,
          type: "file",
        };
      }
    });
  };

  const initSourceStructure = () => {
    let ret = {
      checked: 0,
      children: [],
      isOpen: true,
      name: contractName,
      id: 0,
    };

    if (contract?.sourceStructure) {
      const structure = JSON.parse(contract.sourceStructure);

      ret.children = composeStructure(structure);
    }

    setSourceStructure(ret);
  };

  const getWriteContractStruct = (actionName: string) => {
    if (!abi) {
      return;
    }

    for (let i = 0; i < abi.structs.length; i++) {
      if (abi.structs[i].name === actionName) {
        return abi.structs[i];
      }
    }
  };

  const resetConnection = () => {
    setConnectedAddress(undefined);
  };

  const handleNetworkChanged = (chainID: number) => {
    if (chainID !== parseInt(process.env.REACT_APP_CHAIN_ID!)) {
      resetConnection();
      return;
    }
  };

  const handleConnected = async (
    chainID: number,
    origin: string,
    accountName: string,
    pubKey: string,
    iconType: number
  ) => {
    if (origin !== window.location.origin) {
      return;
    }

    setShowProgress(false);
    if (chainID !== parseInt(process.env.REACT_APP_CHAIN_ID!)) {
      toast.warn("ChainID is not correct.");
      return;
    }

    setConnectedAddress(accountName);
  };

  const handleRejectConnection = () => {
    setShowProgress(false);

    resetConnection();
  };

  const handleDisconnected = async (
    chainID: number,
    origin: string,
    accountName: string
  ) => {
    if (origin === window.location.origin) {
      if (connectedAddress === accountName) {
        resetConnection();
      }
    }
  };

  const handleAccountChanged = async (chainID: number) => {
    if (chainID !== parseInt(process.env.REACT_APP_CHAIN_ID!)) {
      resetConnection();
      return;
    }

    const accountData = await ExtensionHandler.instance().getCurrentAccount(
      parseInt(process.env.REACT_APP_CHAIN_ID!)
    );

    if (accountData.result) {
      setConnectedAddress(accountData.data.account);
    } else {
      resetConnection();
    }
  };

  const handleConnect = async () => {
    setShowProgress(true);

    const handler = ExtensionHandler.instance();

    if (!connectedAddress) {
      handler.connect(parseInt(process.env.REACT_APP_CHAIN_ID!));
    }
  };

  const handleDisconnect = () => {
    ExtensionHandler.instance().disconnect(
      parseInt(process.env.REACT_APP_CHAIN_ID!),
      connectedAddress!
    );
  };

  return (
    <>
      <Title title={`Contract`} smallTitle={contractName ?? ""} />

      <Container className="container-xxl pt-5 pb-12">
        <Grid container spacing={2} className="g-4 mb-4">
          <ContractInfo
            title="Overview"
            value={0}
            usdQuote={quoteUSD?.marketCapPrice ?? 0}
          />
          <MoreInfo
            title="More Info"
            sentTx={"78040312e3b6896b35..."}
            receivedTx={"78040312e3b6896b35..."}
          />
        </Grid>

        <Box className="d-flex align-items-start flex-wrap pb-3">
          <ul
            className="nav nav-pills text-nowrap snap-x-mandatory overflow-x-auto flex-nowrap scrollbar-custom flex-grow-1 pt-4 gap-2 nav_tabs1"
            id="nav_tabs"
            role="tablist"
            style={{ maxWidth: "-webkit-fill-available" }}
          >
            <li
              id="ContentPlaceHolder1_li_transactions"
              className="nav-item snap-align-start"
              role="presentation"
            >
              <a
                className={`nav-link ${
                  selectedTab === TAB.TRANSACTION ? "active" : ""
                }`}
                id="tab-1"
                data-bs-toggle="pill"
                data-bs-target="#transactions"
                type="button"
                role="tab"
                aria-controls="transactions"
                aria-selected="false"
                onClick={() => setSelectedTab(TAB.TRANSACTION)}
                href="#"
              >
                Transactions
              </a>
            </li>
            <li
              id="ContentPlaceHolder1_li_contracts"
              className="nav-item snap-align-start"
              role="presentation"
            >
              <a
                className={`nav-link position-relative ${
                  selectedTab === TAB.CONTRACT ? "active" : ""
                }`}
                id="tab-7"
                data-bs-toggle="pill"
                data-bs-target="#contracts"
                type="button"
                role="tab"
                aria-controls="contracts"
                aria-selected="true"
                onClick={() => setSelectedTab(TAB.CONTRACT)}
                href="#"
              >
                Contract{" "}
                {contract?.verified && (
                  <CheckCircleIcon
                    className="fas fa-check-circle text-success position-absolute"
                    sx={{
                      top: "-0.4rem",
                      right: "-0.4rem",
                      width: "14px",
                      height: "14px",
                    }}
                  />
                )}
              </a>
            </li>
            <li
              id="ContentPlaceHolder1_li_events"
              className="nav-item snap-align-start"
              role="presentation"
            >
              <a
                className={`nav-link ${
                  selectedTab === TAB.EVENTS ? "active" : ""
                }`}
                id="tab-8"
                data-bs-toggle="pill"
                data-bs-target="#events"
                type="button"
                role="tab"
                aria-controls="events"
                aria-selected="false"
                onClick={() => setSelectedTab(TAB.EVENTS)}
                href="#"
              >
                Events
              </a>
            </li>
          </ul>
        </Box>

        {selectedTab === TAB.TRANSACTION && (
          <Box className="card">
            <Box className="card-body">
              <Box className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                <Box>
                  <span className="text-dark gap-1">
                    {total.toLocaleString()} transactions found
                  </span>
                  <Box className="d-block text-muted small">
                    (Showing the last 500k records)
                  </Box>
                </Box>
                <Box className="d-flex flex-wrap align-items-center justify-content-between gap-2">
                  <Pagination
                    limit={limit}
                    page={page}
                    setPage={setPage}
                    total={total}
                  />
                </Box>
              </Box>
            </Box>

            <TableContainer
              component={Paper}
              className="box-shadow-0 table-striped"
            >
              <Table sx={{ minWidth: 650 }} aria-label="simple table">
                <TableHead>
                  <TableRow>
                    <TableCell width={10} align="center">
                      <Tooltip
                        title={"See preview of the transaction details."}
                        placement="right"
                      >
                        <HelpOutlineIcon className="text-icon text-muted" />
                      </Tooltip>
                    </TableCell>
                    <TableCell align="left" width={1}>
                      Txn Hash
                    </TableCell>
                    <TableCell align="left">
                      <Box className="d-flex align-items-center">
                        Method{" "}
                        <Tooltip
                          title={
                            "Function executed based on decoded input data. For unidentified functions, method ID is displayed instead."
                          }
                          placement="right"
                        >
                          <HelpOutlineIcon className="text-icon text-muted" />
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell align="left">Block</TableCell>
                    <TableCell align="left">
                      <Link>Age</Link>
                    </TableCell>
                    <TableCell align="left">From</TableCell>
                    <TableCell align="left"></TableCell>
                    <TableCell align="left">To</TableCell>
                    <TableCell align="left">Value</TableCell>
                    <TableCell align="left">
                      <Link>Txn Fee</Link>
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {transactions.map((data, index) => {
                    let value = "";
                    let toValue = "";
                    if (
                      data.actionName === "transfer" ||
                      data.actionName === "issue" ||
                      data.actionName === "swap" ||
                      data.actionName === "multitransfer"
                    ) {
                      value = JSON.parse(data.data).quantity;
                    }
                    if (
                      data.actionName === "swap" ||
                      data.actionName === "multitransfer"
                    ) {
                      toValue = data.swapAmount
                        ? `${data.swapAmount} ${data.swapToken}`
                        : "";
                    }
                    return (
                      <TableRow key={index}>
                        <TableCell align="center">
                          <Box
                            className="js-tnx-preview btn btn-sm btn-white fs-70x content-center mx-auto myFnExpandBox"
                            sx={{
                              width: "1.75rem",
                              height: "1.75rem",
                            }}
                          >
                            <RemoveRedEyeOutlinedIcon className="small-icon" />
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box className="hash-tag text-truncate">
                            <Link
                              href={`/tx/${data.txId}`}
                              className="myFnExpandBox_searchVal"
                            >
                              {data.txId}
                            </Link>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box className="d-block badge bg-light border border-dark dark:border-white border-opacity-10 text-dark fw-normal text-truncate w-100 py-1.5">
                            <Tooltip
                              describeChild
                              title="Transfer"
                              placement="top"
                            >
                              <span>{data.actionName}</span>
                            </Tooltip>
                          </Box>
                        </TableCell>

                        <TableCell>
                          <Link href={`/block/${data.blockNum}`}>
                            {data.blockNum}
                          </Link>
                        </TableCell>
                        <TableCell>
                          <Tooltip
                            title={`${CalculatePassTime(
                              new Date(data.timestamp.substring(0, 19))
                            )} ago`}
                            placement="top"
                          >
                            <Box rel="tooltip">
                              {CalculatePassTime(
                                new Date(data.timestamp.substring(0, 19))
                              )}{" "}
                              ago
                            </Box>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Box className="d-flex d-flex align-items-center gap-1">
                            {data.sender.includes("upcx.") && (
                              <Tooltip title="Contract" placement="top">
                                <AssignmentIcon className="text-icon text-secondary" />
                              </Tooltip>
                            )}
                            {data.sender === contractName ? (
                              <Box>{data.sender}</Box>
                            ) : (
                              <Link
                                href={`/address/${data.sender}`}
                                className="hash-tag text-truncate border px-1 border-0"
                              >
                                {data.sender}
                              </Link>
                            )}

                            <Tooltip title="Copy Address" placement="top">
                              <Link className="js-clipboard link-secondary ">
                                <ContentCopyIcon
                                  className="text-icon"
                                  onClick={() =>
                                    navigator.clipboard.writeText(data.sender)
                                  }
                                />
                              </Link>
                            </Tooltip>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box
                            className="bg-success bg-opacity-10 border border-success border-opacity-25 text-success fs-70x content-center rounded-pill mx-auto"
                            sx={{
                              width: "1.5rem",
                              height: "1.5rem",
                            }}
                          >
                            <ArrowRightAltIcon className="text-icon" />
                          </Box>
                        </TableCell>

                        <TableCell>
                          <Box className="d-flex d-flex align-items-center gap-1">
                            <Box className="d-flex align-items-center gap-1 border px-1 border-0">
                              {data.receiver.includes("upcx.") && (
                                <Tooltip title="Contract" placement="top">
                                  <AssignmentIcon className="text-icon text-secondary" />
                                </Tooltip>
                              )}

                              <Tooltip
                                title="Public Tag: Fee Recipient: 0xe68...127 (0xe688b84b23f322a994a53dbf8e15fa82cdb71127)"
                                placement="top"
                              >
                                {data.receiver === contractName ? (
                                  <Box>{data.receiver}</Box>
                                ) : (
                                  <Link
                                    href={`/address/${data.receiver}`}
                                    className=""
                                    data-bs-toggle="tooltip"
                                    data-bs-trigger="hover"
                                    data-bs-html="true"
                                    data-bs-title="0x1dde41f92d3f7a46ce6f6de24ab3a94a45a3d708"
                                  >
                                    {data.receiver}
                                  </Link>
                                )}
                              </Tooltip>
                            </Box>

                            {data.receiver !== "" && (
                              <Tooltip title="Copy Address" placement="top">
                                <Link className="js-clipboard link-secondary ">
                                  <ContentCopyIcon
                                    className="text-icon"
                                    onClick={() =>
                                      navigator.clipboard.writeText(
                                        data.receiver
                                      )
                                    }
                                  />
                                </Link>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Tooltip title={value} placement="top">
                            <Box>
                              {value}
                              {toValue && (
                                <>
                                  &nbsp;
                                  <ArrowRightAltIcon className="text-icon" />
                                  &nbsp;
                                  {toValue}
                                </>
                              )}
                            </Box>
                          </Tooltip>
                        </TableCell>
                        <TableCell className="text-muted">{`${data.fee.toFixed(
                          5
                        )} UPC`}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            <Box className="card-body">
              <Box className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                <Box
                  id="ContentPlaceHolder1_pageRecords"
                  className="d-flex align-items-center gap-2 text-nowrap text-muted"
                >
                  Show:
                  <FormControl sx={{ m: 1, minWidth: 70 }}>
                    <Select
                      value={limit.toString()}
                      onChange={handleChange}
                      displayEmpty
                      size="small"
                      sx={{
                        fontSize: "12px",
                      }}
                    >
                      <MenuItem
                        value={10}
                        sx={{
                          fontSize: "12px;",
                        }}
                      >
                        10
                      </MenuItem>
                      <MenuItem
                        sx={{
                          fontSize: "12px;",
                        }}
                        value={25}
                      >
                        25
                      </MenuItem>
                      <MenuItem
                        sx={{
                          fontSize: "12px;",
                        }}
                        value={50}
                      >
                        50
                      </MenuItem>
                      <MenuItem
                        sx={{
                          fontSize: "12px;",
                        }}
                        value={100}
                      >
                        100
                      </MenuItem>
                    </Select>
                  </FormControl>
                  Records
                </Box>
                <Box className="d-flex flex-wrap align-items-center justify-content-between gap-2">
                  <Pagination
                    limit={limit}
                    page={page}
                    setPage={setPage}
                    total={total}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        )}
        {selectedTab === TAB.CONTRACT && (
          <Box className="card p-5 mb-3">
            {!contract?.verified ? (
              <Box
                className="tab-pane fade active show"
                id="code"
                role="tabpanel"
                sx={{ display: "visible" }}
              >
                <span className="mb-3">
                  <span className="mb-0">
                    <GppMaybeIcon
                      className="fa fa-info-circle text-secondary"
                      sx={{ width: "18px", height: "18px" }}
                    />
                  </span>{" "}
                  Are you the contract creator?{" "}
                  <a href={`/contract/verify/${contractName}`}>
                    <b>Verify and Publish</b>
                  </a>{" "}
                  your contract source code today!
                  <br />
                  <br />
                </span>

                <Box id="Boxidcode">
                  <pre
                    className="wordwrap scrollbar-custom"
                    style={{ height: "15pc" }}
                  >
                    {contract?.code ?? ""}
                  </pre>
                </Box>
              </Box>
            ) : (
              <>
                <Box className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center bg-white py-1 mb-3 gap-3">
                  <ul
                    className="nav nav-pills gap-2 nav-subtabs"
                    id="nav_subtabs"
                    role="tablist"
                  >
                    <li
                      id="ContentPlaceHolder1_li_code"
                      className="nav-item snap-align-start"
                      role="presentation"
                    >
                      <a
                        className={`nav-link ${
                          selectedSubTab === SUB_TAB.CODE ? "show active" : ""
                        }`}
                        data-bs-toggle="pill"
                        data-bs-target="#code"
                        type="button"
                        role="tab"
                        aria-controls="code"
                        aria-selected="true"
                        href="#"
                        onClick={() => setSelectedSubTab(SUB_TAB.CODE)}
                      >
                        <span>Code</span>
                      </a>
                    </li>
                    <li
                      id="ContentPlaceHolder1_li_readContract"
                      className="nav-item snap-align-start"
                      role="presentation"
                    >
                      <a
                        className={`nav-link ${
                          selectedSubTab === SUB_TAB.READ_CONTRACT
                            ? "show active"
                            : ""
                        }`}
                        href="#"
                        data-bs-toggle="pill"
                        data-bs-target="#readContract"
                        type="button"
                        role="tab"
                        aria-controls="readContract"
                        aria-selected="false"
                        onClick={() => setSelectedSubTab(SUB_TAB.READ_CONTRACT)}
                      >
                        Read{" "}
                        <span className="d-none d-sm-inline-block">
                          Contract
                        </span>
                      </a>
                    </li>
                    <li
                      id="ContentPlaceHolder1_li_writeContract"
                      className="nav-item snap-align-start"
                      role="presentation"
                    >
                      <a
                        className={`nav-link ${
                          selectedSubTab === SUB_TAB.WRITE_CONTRACT
                            ? "show active"
                            : ""
                        }`}
                        href="#"
                        data-bs-toggle="pill"
                        data-bs-target="#writeContract"
                        type="button"
                        role="tab"
                        aria-controls="writeContract"
                        aria-selected="false"
                        onClick={() =>
                          setSelectedSubTab(SUB_TAB.WRITE_CONTRACT)
                        }
                      >
                        Write{" "}
                        <span className="d-none d-sm-inline-block">
                          Contract
                        </span>
                      </a>
                    </li>
                  </ul>
                </Box>
                {selectedSubTab === SUB_TAB.CODE && (
                  <>
                    <Box className="col-md-12">
                      <h3 className="h6 text-dark fw-bold pt-3 pb-2 mb-4">
                        <span data-bs-toggle="tooltip" className="d-flex">
                          <CheckCircleIcon
                            className="fa fa-check-circle text-success"
                            sx={{
                              width: "16px",
                              height: "16px",
                              my: "auto",
                              mr: "5px",
                            }}
                          />
                          <strong>
                            Contract Source Code Verified{" "}
                            <span className="fw-normal text-muted">
                              (Exact Match)
                            </span>
                          </strong>
                        </span>
                      </h3>
                    </Box>
                    <Box className="col-md-6 mb-4">
                      <Box className="row align-items-center">
                        <Box className="col-5 col-lg-4 mb-1 mb-md-0">
                          Contract Name:
                        </Box>
                        <Box className="col-7 col-lg-8">
                          <span className="h6 fw-bold mb-0">
                            {contractName}
                          </span>
                        </Box>
                      </Box>
                    </Box>
                    <Box className="row ms-0">
                      <Box
                        className="col-md-4 card pt-1"
                        sx={{ height: "30pc !important" }}
                      >
                        <FolderTree
                          data={sourceStructure}
                          showCheckbox={false}
                          readOnly
                          onNameClick={handleSourceFileClicked}
                        />
                      </Box>
                      <Box className="col-md-8">
                        <Box id="Boxidcode">
                          <pre
                            className="wordwrap scrollbar-custom"
                            style={{ height: "30pc" }}
                          >
                            {selectedFileContent ?? ""}
                          </pre>
                        </Box>
                      </Box>
                    </Box>
                    <Box className="d-flex flex-column flex-sm-row justify-content-between align-items-sm-center gap-2 mb-2">
                      <h4 className="card-header-title d-flex">
                        <ListIcon
                          className="far fa-code text-secondary me-1"
                          sx={{
                            width: "18px",
                            height: "18px",
                            my: "auto",
                            mr: "5px",
                          }}
                        />
                        Contract ABI
                      </h4>
                    </Box>
                    <Box id="Boxidcode">
                      <pre
                        className="wordwrap scrollbar-custom"
                        style={{ height: "15pc" }}
                      >
                        {abi ? JSON.stringify(abi) : ""}
                      </pre>
                    </Box>
                    <Box className="d-flex flex-column flex-sm-row justify-content-between align-items-sm-center gap-2 mb-2">
                      <h4 className="card-header-title d-flex">
                        <CodeIcon
                          className="far fa-code text-secondary me-1"
                          sx={{
                            width: "18px",
                            height: "18px",
                            my: "auto",
                            mr: "5px",
                          }}
                        />
                        Contract Creation Code
                      </h4>
                    </Box>
                    <Box id="Boxidcode">
                      <pre
                        className="wordwrap scrollbar-custom"
                        style={{ height: "15pc" }}
                      >
                        {contract?.code ?? ""}
                      </pre>
                    </Box>
                  </>
                )}
                {selectedSubTab === SUB_TAB.READ_CONTRACT && (
                  <>
                    <div className="d-flex justify-content-between mb-3">
                      <p className="ms-1 me-3 mb-1">
                        <DescriptionIcon
                          className="far fa-code text-secondary me-1"
                          sx={{
                            width: "18px",
                            height: "18px",
                            margin: "-5px 5px auto 0px",
                          }}
                        />
                        Read Contract Information
                      </p>
                    </div>
                    <div id="readContractAccordion">
                      {abi &&
                        abi.tables.map((item: any, index: number) => {
                          return (
                            <ReadContractItem
                              item={item}
                              key={index}
                              contractName={contract?.name ?? ""}
                            />
                          );
                        })}
                    </div>
                  </>
                )}
                {selectedSubTab === SUB_TAB.WRITE_CONTRACT && (
                  <>
                    <div className="d-flex justify-content-between mb-3">
                      <p className="ms-1 me-3 mb-1">
                        <button
                          type="button"
                          className="btn btn-white focus-shadow-0"
                          id="connectStatus"
                          onClick={
                            connectedAddress ? handleDisconnect : handleConnect
                          }
                        >
                          <AccountBalanceWalletIcon
                            className="far fa-code text-secondary me-1"
                            sx={{
                              width: "18px",
                              height: "18px",
                              margin: "-5px 5px auto 0px",
                            }}
                          />
                          {connectedAddress ?? "Connect Wallet"}
                        </button>
                      </p>
                    </div>
                    <div id="readContractAccordion">
                      {abi &&
                        abi.actions.map((item: any, index: number) => {
                          return (
                            <WriteContractItem
                              item={getWriteContractStruct(item.name)}
                              key={index}
                              contractName={contract?.name ?? ""}
                              connectedAddress={connectedAddress}
                            />
                          );
                        })}
                    </div>
                  </>
                )}
              </>
            )}
          </Box>
        )}

        {selectedTab === TAB.EVENTS && (
          <Box className="card">
            <Box className="card-body">
              <Box className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                <Box>
                  <span className="text-dark gap-1">10000 Events found</span>
                  <Box className="d-block text-muted small">
                    (Showing the last 500k records)
                  </Box>
                </Box>
                <Box className="d-flex flex-wrap align-items-center justify-content-between gap-2">
                  <Pagination
                    limit={limit}
                    page={page}
                    setPage={setPage}
                    total={total}
                  />
                </Box>
              </Box>
            </Box>

            <TableContainer
              component={Paper}
              className="box-shadow-0 table-striped"
            >
              <Table sx={{ minWidth: 650 }} aria-label="simple table">
                <TableHead>
                  <TableRow>
                    <TableCell width={10} align="center">
                      <Tooltip
                        title={"See preview of the transaction details."}
                        placement="right"
                      >
                        <HelpOutlineIcon className="text-icon text-muted" />
                      </Tooltip>
                    </TableCell>
                    <TableCell align="left" width={1}>
                      Txn Hash
                    </TableCell>

                    <TableCell align="left">Block</TableCell>
                    <TableCell align="left">
                      <Link>Age</Link>
                    </TableCell>
                    <TableCell align="center">
                      <Box className="align-items-center">Method </Box>
                    </TableCell>
                    <TableCell align="left">
                      <Box className="d-flex align-items-center">
                        <DvrIcon
                          className="text-icon text-muted"
                          sx={{ mr: "10px" }}
                        />{" "}
                        Logs
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {transactions.map((data, index) => {
                    let value = "";
                    let toValue = "";
                    if (
                      data.actionName === "transfer" ||
                      data.actionName === "issue" ||
                      data.actionName === "swap" ||
                      data.actionName === "multitransfer"
                    ) {
                      value = JSON.parse(data.data).quantity;
                    }
                    if (
                      data.actionName === "swap" ||
                      data.actionName === "multitransfer"
                    ) {
                      toValue = data.swapAmount
                        ? `${data.swapAmount} ${data.swapToken}`
                        : "";
                    }
                    return (
                      <TableRow key={index}>
                        <TableCell align="center">
                          <Box
                            className="js-tnx-preview btn btn-sm btn-white fs-70x content-center mx-auto myFnExpandBox"
                            sx={{
                              width: "1.75rem",
                              height: "1.75rem",
                            }}
                          >
                            <RemoveRedEyeOutlinedIcon className="small-icon" />
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box className="hash-tag text-truncate">
                            <Link
                              href={`/tx/${data.txId}`}
                              className="myFnExpandBox_searchVal"
                            >
                              {data.txId}
                            </Link>
                          </Box>
                        </TableCell>

                        <TableCell>
                          <Link href={`/block/${data.blockNum}`}>
                            {data.blockNum}
                          </Link>
                        </TableCell>
                        <TableCell>
                          <Tooltip
                            title={`${CalculatePassTime(
                              new Date(data.timestamp.substring(0, 19))
                            )} ago`}
                            placement="top"
                          >
                            <Box rel="tooltip">
                              {CalculatePassTime(
                                new Date(data.timestamp.substring(0, 19))
                              )}{" "}
                              ago
                            </Box>
                          </Tooltip>
                        </TableCell>

                        <TableCell>
                          <Box
                            className="d-flex d-flex align-items-center gap-1"
                            flexDirection="column"
                          >
                            <span
                              className="d-block badge bg-light border border-dark dark:border-white border-opacity-10 text-dark fw-normal text-truncate w-100 py-1.5 mt-1 text-dark"
                              data-bs-toggle="tooltip"
                            >
                              0xa9059cbb
                            </span>
                            <span
                              className="d-flex text-truncate"
                              style={{ maxWidth: "12rem" }}
                            >
                              <Box
                                className="js-tnx-preview btn btn-sm btn-white fs-70x content-center mx-auto myFnExpandBox"
                                sx={{
                                  width: "1.75rem",
                                  height: "1.75rem",
                                  marginRight: "5px !important",
                                }}
                              >
                                <RemoveRedEyeOutlinedIcon className="small-icon" />
                              </Box>
                              <span
                                className="text-monospace"
                                style={{ margin: "auto" }}
                              >
                                <a
                                  style={{ color: "inherit" }}
                                  data-bs-toggle="popover"
                                  data-bs-placement="left"
                                  data-bs-content="transfer(address,uint256)"
                                  href="#"
                                >
                                  transfer(address,uint256){" "}
                                </a>
                              </span>
                            </span>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box className="d-block align-items-center gap-1">
                            <a
                              className="accordion-toggle text-monospace"
                              data-bs-toggle="collapse"
                              data-bs-target="#demo1"
                              style={{ cursor: "pointer" }}
                              href="#"
                            >
                              <span
                                style={{ color: "#3498db" }}
                                data-bs-toggle="tooltip"
                              >
                                SetStakingPeriod
                              </span>{" "}
                            </a>{" "}
                            <span className="d-inline-block text-monospace mb-1">
                              (<span className="text-success">uint256</span>{" "}
                              <span className="text-danger">id</span>,{" "}
                              <span className="text-success">tuple[]</span>{" "}
                              <span className="text-danger">periods</span>)
                            </span>
                            <br />
                            <Box
                              id="demo1"
                              className="collapse text-monospace"
                            ></Box>
                            <span style={{ fontFamily: "Monospace" }}>
                              <span className="text-muted">
                                [topic0]
                                0xe250ae701c4dd28188ffca44dffb51154fe04268273c6db802cc1883226d6e87
                              </span>
                              <br />
                              <table className="table-borderless mt-2">
                                <tbody>
                                  {Array(6)
                                    .fill(1)
                                    .map((value, index) => (
                                      <tr className="no-hover" key={index}>
                                        <td width="52px" className="p-0">
                                          <span className="dropdown">
                                            <button
                                              className="btn btn-sm btn-white d-flex"
                                              type="button"
                                              id="ddhex_1_1"
                                            >
                                              <span id="convert_button_1_1">
                                                Hex
                                              </span>
                                              <KeyboardArrowDownIcon
                                                sx={{
                                                  width: "18px",
                                                  height: "18px",
                                                }}
                                              />
                                            </button>
                                            <Menu open={false}>
                                              <MenuItem>Hex</MenuItem>
                                              <MenuItem>Number</MenuItem>
                                              <MenuItem>Text</MenuItem>
                                              <MenuItem>Address</MenuItem>
                                            </Menu>
                                            <span></span>
                                          </span>
                                        </td>
                                        <td width="10px" className="p-2">
                                          <ArrowForwardIcon className="fas fa-arrow-right" />
                                        </td>
                                        <td className="p-2">
                                          &nbsp;
                                          <span id="chunk_1_1">
                                            0000000000000000000000000000000000000000000000000000000000000003
                                          </span>
                                          <span
                                            style={{ display: "none" }}
                                            id="chunk_ori_1_1"
                                          >
                                            0000000000000000000000000000000000000000000000000000000000000003
                                          </span>
                                        </td>
                                      </tr>
                                    ))}
                                </tbody>
                              </table>
                            </span>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            <Box className="card-body">
              <Box className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                <Box
                  id="ContentPlaceHolder1_pageRecords"
                  className="d-flex align-items-center gap-2 text-nowrap text-muted"
                >
                  Show:
                  <FormControl sx={{ m: 1, minWidth: 70 }}>
                    <Select
                      value={limit.toString()}
                      onChange={handleChange}
                      displayEmpty
                      size="small"
                      sx={{
                        fontSize: "12px",
                      }}
                    >
                      <MenuItem
                        value={10}
                        sx={{
                          fontSize: "12px;",
                        }}
                      >
                        10
                      </MenuItem>
                      <MenuItem
                        sx={{
                          fontSize: "12px;",
                        }}
                        value={25}
                      >
                        25
                      </MenuItem>
                      <MenuItem
                        sx={{
                          fontSize: "12px;",
                        }}
                        value={50}
                      >
                        50
                      </MenuItem>
                      <MenuItem
                        sx={{
                          fontSize: "12px;",
                        }}
                        value={100}
                      >
                        100
                      </MenuItem>
                    </Select>
                  </FormControl>
                  Records
                </Box>
                <Box className="d-flex flex-wrap align-items-center justify-content-between gap-2">
                  <Pagination
                    limit={limit}
                    page={page}
                    setPage={setPage}
                    total={total}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        )}
      </Container>
    </>
  );
};
